import { useEffect, useState } from "react";
import {
  BellSimple,
  CalendarBlank,
  FileText,
  Funnel,
  Images,
  Newspaper,
  PenNib,
  SquaresFour,
} from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_STATS = {
  reports: null,
  kybiaReports: null,
  writtenPositions: null,
  gallery: null,
  leads: null,
  petitions: null,
  news: null,
  popups: null,
  contentSections: null,
};

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso || "";
  }
}

async function safeFetchArray(path) {
  try {
    const res = await fetch(`${BACKEND_URL}${path}`);
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

async function safeFetchCount(path) {
  try {
    const res = await fetch(`${BACKEND_URL}${path}`);
    const data = await res.json();
    return Number(data?.count) || 0;
  } catch {
    return 0;
  }
}

function StatCard({ icon: Icon, label, count, color, caption }) {
  return (
    <div className="rounded-xl border p-5 flex items-center gap-4" style={{ backgroundColor: "#fff", borderColor: "rgba(26,92,55,0.1)" }}>
      <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}15` }}>
        <Icon size={24} weight="duotone" style={{ color }} />
      </div>
      <div className="min-w-0">
        <p className="text-3xl font-heading font-semibold leading-none" style={{ color: "#1C211F" }}>
          {count === null ? <span className="text-xl animate-pulse" style={{ color: "#7A8A84" }}>--</span> : count}
        </p>
        <p className="text-sm mt-1 font-medium" style={{ color: "#1C211F" }}>{label}</p>
        {caption && <p className="text-xs mt-0.5 truncate" style={{ color: "#7A8A84" }}>{caption}</p>}
      </div>
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <div className="rounded-xl border p-5" style={{ backgroundColor: "#fff", borderColor: "rgba(26,92,55,0.1)" }}>
      <h4 className="font-heading text-lg font-medium mb-4" style={{ color: "#1C211F" }}>{title}</h4>
      {children}
    </div>
  );
}

function RecentLeadRow({ lead }) {
  return (
    <div className="flex items-start gap-3 py-3 border-b last:border-b-0" style={{ borderColor: "rgba(26,92,55,0.08)" }}>
      <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0" style={{ backgroundColor: "rgba(26,92,55,0.1)", color: "#1A5C37" }}>
        {(lead.name || "?").slice(0, 1).toUpperCase()}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium truncate" style={{ color: "#1C211F" }}>{lead.name || "Unnamed lead"}</p>
        <p className="text-xs truncate" style={{ color: "#4A5450" }}>{lead.email || lead.phone || "No contact detail"}</p>
        {lead.message && <p className="text-xs mt-1 line-clamp-2" style={{ color: "#7A8A84" }}>{lead.message}</p>}
      </div>
      <span className="text-xs flex items-center gap-1 flex-shrink-0" style={{ color: "#7A8A84" }}>
        <CalendarBlank size={11} weight="duotone" />
        {formatDate(lead.created_at)}
      </span>
    </div>
  );
}

function ActivityRow({ label, value }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b last:border-b-0" style={{ borderColor: "rgba(26,92,55,0.08)" }}>
      <span className="text-sm" style={{ color: "#4A5450" }}>{label}</span>
      <span className="text-sm font-semibold" style={{ color: "#1C211F" }}>{value}</span>
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(EMPTY_STATS);
  const [recentLeads, setRecentLeads] = useState([]);
  const [recentPetitions, setRecentPetitions] = useState([]);

  useEffect(() => {
    let active = true;

    async function loadDashboard() {
      const [
        reports,
        kybiaReports,
        gallery,
        leads,
        petitions,
        petitionCount,
        news,
        popups,
        contentSections,
      ] = await Promise.all([
        safeFetchArray("/api/reports"),
        safeFetchArray("/api/kybia-reports"),
        safeFetchArray("/api/gallery"),
        safeFetchArray("/api/leads"),
        safeFetchArray("/api/petition-signatures?page=1&limit=5"),
        safeFetchCount("/api/petition-signatures/count"),
        safeFetchArray("/api/news"),
        safeFetchArray("/api/popups"),
        safeFetchArray("/api/content-sections"),
      ]);

      if (!active) return;

      setStats({
        reports: reports.length,
        kybiaReports: kybiaReports.length,
        writtenPositions: reports.length + kybiaReports.length,
        gallery: gallery.length,
        leads: leads.length,
        petitions: petitionCount,
        news: news.length,
        popups: popups.length,
        contentSections: contentSections.length,
      });
      setRecentLeads(leads.slice(0, 5));
      setRecentPetitions(petitions.slice(0, 5));
    }

    loadDashboard();
    return () => { active = false; };
  }, []);

  return (
    <div>
      <div className="mb-8">
        <p className="text-xs uppercase tracking-[0.18em] font-medium mb-2" style={{ color: "#B85C38" }}>Admin Overview</p>
        <h3 className="font-heading text-2xl font-medium" style={{ color: "#1C211F" }}>Dashboard</h3>
        <p className="text-sm mt-1" style={{ color: "#4A5450" }}>A quick pulse check across petitions, leads, reports, and public content.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <StatCard icon={PenNib} label="Petitions" count={stats.petitions} color="#1A5C37" caption="Signatures received" />
        <StatCard icon={Funnel} label="Leads" count={stats.leads} color="#B85C38" caption="Get Involved submissions" />
        <StatCard icon={FileText} label="Written Positions" count={stats.writtenPositions} color="#1A5C37" caption="Reports and KYBIA reports" />
        <StatCard icon={FileText} label="KYBIA Reports" count={stats.kybiaReports} color="#4A5450" caption="Public Reports page" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
        <StatCard icon={FileText} label="Reports" count={stats.reports} color="#1A5C37" caption="Research page reports" />
        <StatCard icon={Images} label="Gallery Images" count={stats.gallery} color="#1A5C37" caption="Uploaded gallery items" />
        <StatCard icon={Newspaper} label="News" count={stats.news} color="#B85C38" caption="News links published" />
        <StatCard icon={SquaresFour} label="About Sections" count={stats.contentSections} color="#4A5450" caption="Content sections" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2">
          <Panel title="Recent Leads">
            {recentLeads.length === 0 ? (
              <div className="py-10 text-center">
                <Funnel size={28} weight="duotone" style={{ color: "#1A5C37", margin: "0 auto 10px" }} />
                <p className="text-sm font-medium" style={{ color: "#1C211F" }}>No recent leads</p>
                <p className="text-xs mt-1" style={{ color: "#7A8A84" }}>New Get Involved submissions will appear here.</p>
              </div>
            ) : (
              <div>
                {recentLeads.map((lead) => <RecentLeadRow key={lead.id} lead={lead} />)}
              </div>
            )}
          </Panel>
        </div>

        <Panel title="Activity Snapshot">
          <ActivityRow label="Recent petition signatures" value={recentPetitions.length} />
          <ActivityRow label="Homepage pop-ups" value={stats.popups ?? "--"} />
          <ActivityRow label="News items" value={stats.news ?? "--"} />
          <ActivityRow label="Content sections" value={stats.contentSections ?? "--"} />
          <ActivityRow label="Gallery images" value={stats.gallery ?? "--"} />
          <div className="mt-5 rounded-lg p-4" style={{ backgroundColor: "rgba(26,92,55,0.06)" }}>
            <div className="flex items-center gap-2 mb-2">
              <BellSimple size={16} weight="duotone" style={{ color: "#1A5C37" }} />
              <p className="text-sm font-medium" style={{ color: "#1C211F" }}>Engagement health</p>
            </div>
            <p className="text-xs leading-relaxed" style={{ color: "#4A5450" }}>
              Leads and petition signatures are the strongest indicators of active citizen engagement. Use recent leads for follow-up and exports for offline review.
            </p>
          </div>
        </Panel>
      </div>
    </div>
  );
}
